/**
 * 방명록 표준 컨트롤러.
 *
 * 디자이너 DOM:
 *  - 리스트: [data-guestbook-list] 또는 #guestbookList
 *  - 작성 버튼: [data-guestbook-open] 또는 #guestbookOpenBtn/#guestbookWriteBtn
 *  - 작성 모달 overlay: #guestbookOverlay 또는 [data-guestbook-overlay]
 *  - 작성 form: [data-guestbook-form] 또는 #guestbookForm
 *
 * 카드 렌더링은 DOM API(createElement + textContent)로만 수행.
 * API: GET/POST /api/invitations/{id}/guestbook. 제출 시 password 필수.
 * 작성자 삭제: POST /api/invitations/{id}/guestbook/{entryId}/delete (작성 시 비밀번호 검증, FIKET-299).
 *
 * 참고: 이 컨트롤러는 B형 DOM([data-guestbook-form]/.guestbook-card/pagination)을
 * 전제한다. 신규 LLM-import 템플릿만 이 규약을 쓰고, 자사 1st-party 템플릿
 * (drawing 등)은 A형 DOM이라 여기서 findForm() 이 null → 방명록은 web 의
 * lib/viewerScript.ts(buildViewerScript)가 처리한다. 자세한 분담은 그 헤더 참조.
 */
export const GUESTBOOK_CONTROLLER_SCRIPT = `
<script data-fw-gb-controller>
(function () {
  var INVITATION_ID = {{ invitation.id | default: '' | json }};
  var MODE = {{ viewerMode | default: 'live' | json }};
  var API_BASE = INVITATION_ID ? '/api/invitations/' + INVITATION_ID : '';
  var PAGE_SIZE = 5;
  var page = 1;

  // 미리보기(preview/disabled) 전용 샘플 방명록 — 실제 데이터 노출 없이 디자인 확인용
  function sampleDate(daysAgo) {
    return new Date(Date.now() - daysAgo * 86400000).toISOString();
  }
  var SAMPLE_ENTRIES = [
    { name: '김민준', message: '결혼 진심으로 축하해! 두 사람 늘 행복하길 바랄게 💕', createdAt: sampleDate(7) },
    { name: '박서연', message: '두 분의 결혼을 진심으로 축하드립니다. 늘 사랑하며 사세요!', createdAt: sampleDate(3) },
    { name: '이도윤', message: '친구야 결혼 축하한다! 신혼여행 잘 다녀와~', createdAt: sampleDate(1) },
  ];

  function pick(sel, root) { return (root || document).querySelector(sel); }
  function findList() { return pick('[data-guestbook-list]') || pick('#guestbookList'); }
  function findEmpty() { return pick('#guestbookEmpty') || pick('[data-guestbook-empty]'); }
  function findPag() { return pick('#guestbookPagination') || pick('[data-guestbook-pagination]'); }
  function findForm() { return pick('[data-guestbook-form]') || pick('#guestbookForm'); }
  function findOverlay() { return pick('[data-guestbook-overlay]') || pick('#guestbookOverlay'); }
  function findOpen() { return pick('[data-guestbook-open]') || pick('#guestbookOpenBtn') || pick('#guestbookWriteBtn'); }
  function findClose() { return pick('[data-guestbook-close]') || pick('#guestbookClose') || pick('.rsvp-close', findOverlay()); }

  var toastTimer = null;
  function toast(msg) {
    var t = document.getElementById('__fwToast');
    if (!t) {
      t = document.createElement('div');
      t.id = '__fwToast';
      t.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.85);color:#fff;padding:12px 20px;border-radius:8px;font-size:14px;z-index:9999;opacity:0;transition:opacity .2s;pointer-events:none;';
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.style.opacity = '1';
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { t.style.opacity = '0'; }, 1800);
  }
  function formatDate(iso) {
    try { var d = new Date(iso); return d.getFullYear() + '.' + String(d.getMonth() + 1).padStart(2, '0') + '.' + String(d.getDate()).padStart(2, '0'); }
    catch (e) { return ''; }
  }

  function openModal() {
    var ov = findOverlay(); if (!ov) return;
    ov.classList.add('active');
    if (getComputedStyle(ov).display === 'none') ov.style.display = 'flex';
    if (window.__fwScrollLock) window.__fwScrollLock.lock({ allowScrollWithin: ov });
    else document.body.style.overflow = 'hidden';
  }
  function closeModal() {
    var ov = findOverlay(); if (!ov) return;
    ov.classList.remove('active');
    ov.style.display = 'none';
    if (window.__fwScrollLock) window.__fwScrollLock.unlock();
    else document.body.style.overflow = '';
  }

  // 삭제 버튼·메타 그룹·삭제 모달 기본 스타일 — 템플릿 CSS로 오버라이드 가능하도록
  // 인라인이 아닌 <style> 1회 주입으로 제공한다.
  function ensureDeleteStyle() {
    if (document.getElementById('__fwGbDelStyle')) return;
    var st = document.createElement('style');
    st.id = '__fwGbDelStyle';
    st.textContent =
      '.guestbook-card-meta{margin-left:auto;display:inline-flex;align-items:center;}' +
      '.guestbook-card-delete{background:none;border:none;padding:2px 4px;margin-left:6px;font-size:11px;color:#aaa;cursor:pointer;line-height:1;}' +
      '.guestbook-card-delete:hover{color:#666;}' +
      '.__fw-gbdel-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.5);display:none;align-items:center;justify-content:center;z-index:9998;padding:20px;}' +
      '.__fw-gbdel-overlay.active{display:flex;}' +
      '.__fw-gbdel-modal{background:#fff;border-radius:12px;padding:24px 20px;width:100%;max-width:300px;box-shadow:0 8px 30px rgba(0,0,0,0.2);}' +
      '.__fw-gbdel-title{font-size:16px;font-weight:600;margin-bottom:6px;color:#222;}' +
      '.__fw-gbdel-desc{font-size:13px;color:#888;margin-bottom:14px;}' +
      '.__fw-gbdel-input{width:100%;box-sizing:border-box;border:1px solid #ddd;border-radius:8px;padding:10px 12px;font-size:14px;margin-bottom:14px;}' +
      '.__fw-gbdel-btns{display:flex;gap:8px;}' +
      '.__fw-gbdel-btns button{flex:1;padding:10px 0;border-radius:8px;font-size:14px;cursor:pointer;border:1px solid #ddd;background:#fff;color:#555;}' +
      '.__fw-gbdel-btns .__fw-gbdel-confirm{background:#222;border-color:#222;color:#fff;}';
    document.head.appendChild(st);
  }

  function makeCard(item) {
    var card = document.createElement('div'); card.className = 'guestbook-card';
    var header = document.createElement('div'); header.className = 'guestbook-card-header';
    var from = document.createElement('span'); from.className = 'guestbook-card-from'; from.textContent = item.name;
    var date = document.createElement('span'); date.className = 'guestbook-card-date'; date.textContent = formatDate(item.createdAt);
    var del = document.createElement('button');
    del.type = 'button'; del.className = 'guestbook-card-delete';
    del.setAttribute('aria-label', '방명록 삭제'); del.textContent = '삭제';
    del.addEventListener('click', function () { onDelete(item); });
    // 날짜+삭제를 우측 그룹으로 묶는다 (header가 flex space-between이어도 한 덩어리로 정렬)
    var meta = document.createElement('span'); meta.className = 'guestbook-card-meta';
    meta.appendChild(date); meta.appendChild(del);
    header.appendChild(from); header.appendChild(meta);
    var msg = document.createElement('div'); msg.className = 'guestbook-card-message'; msg.textContent = item.message;
    card.appendChild(header); card.appendChild(msg);
    return card;
  }

  function renderCards(items, total) {
    var list = findList(); var empty = findEmpty();
    if (!list) return;
    ensureDeleteStyle();
    while (list.firstChild) list.removeChild(list.firstChild);
    if (!items || items.length === 0) {
      if (empty) empty.style.display = '';
    } else {
      if (empty) empty.style.display = 'none';
      for (var i = 0; i < items.length; i++) list.appendChild(makeCard(items[i]));
    }
    renderPagination(total);
  }
  function renderPagination(total) {
    var pag = findPag(); if (!pag) return;
    var totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));
    while (pag.firstChild) pag.removeChild(pag.firstChild);
    if (totalPages <= 1) { pag.style.display = 'none'; return; }
    pag.style.display = '';
    function makeBtn(label, attrs) {
      var b = document.createElement('button'); b.className = 'gb-page-btn'; b.textContent = label;
      if (attrs) for (var k in attrs) b.setAttribute(k, attrs[k]);
      return b;
    }
    var prev = makeBtn('\\u2039', { 'data-gb-prev': '' }); if (page === 1) prev.disabled = true;
    prev.classList.add('arrow');
    prev.addEventListener('click', function () { if (page > 1) { page--; load(); } });
    pag.appendChild(prev);
    for (var i = 1; i <= totalPages; i++) {
      var b = makeBtn(String(i), { 'data-gb-page': String(i) });
      if (i === page) b.classList.add('active');
      (function (p) { b.addEventListener('click', function () { page = p; load(); }); })(i);
      pag.appendChild(b);
    }
    var next = makeBtn('\\u203A', { 'data-gb-next': '' }); if (page === totalPages) next.disabled = true;
    next.classList.add('arrow');
    next.addEventListener('click', function () { if (page < totalPages) { page++; load(); } });
    pag.appendChild(next);
  }

  function load() {
    // 미리보기/비활성 모드: 실제 방명록 대신 샘플 렌더
    if (MODE === 'preview' || MODE === 'disabled') {
      renderCards(SAMPLE_ENTRIES, SAMPLE_ENTRIES.length);
      return;
    }
    if (!API_BASE) { renderCards([], 0); return; }
    fetch(API_BASE + '/guestbook?page=' + page + '&pageSize=' + PAGE_SIZE, { credentials: 'include' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (d && d.success) renderCards(d.data.entries || [], d.data.total || 0);
      })
      .catch(function () { /* silent */ });
  }

  // 작성자 삭제 — makeCard의 삭제 버튼에서 호출 (함수 선언 호이스팅으로 선언 순서 무관).
  // 비밀번호는 자체 모달로 받는다: 뷰어 iframe sandbox에 allow-modals가 없어
  // 네이티브 prompt 다이얼로그가 조용히 차단되기 때문 (FIKET-299).
  function onDelete(item) {
    if (MODE === 'preview') { toast('미리보기입니다. 청첩장을 생성하면 실제로 저장됩니다.'); return; }
    if (MODE === 'disabled' || !API_BASE || !item.id) { toast('청첩장을 먼저 생성해주세요.'); return; }
    openDeleteModal(item);
  }

  var delTarget = null;
  var delOverlay = null, delInput = null, delConfirm = null;

  function ensureDeleteModal() {
    if (delOverlay) return;
    delOverlay = document.createElement('div'); delOverlay.className = '__fw-gbdel-overlay';
    var modal = document.createElement('div'); modal.className = '__fw-gbdel-modal';
    var title = document.createElement('div'); title.className = '__fw-gbdel-title'; title.textContent = '방명록 삭제';
    var desc = document.createElement('div'); desc.className = '__fw-gbdel-desc'; desc.textContent = '작성하실 때 설정한 비밀번호를 입력해주세요.';
    var form = document.createElement('form');
    delInput = document.createElement('input');
    delInput.type = 'password'; delInput.className = '__fw-gbdel-input';
    delInput.maxLength = 20; delInput.autocomplete = 'off'; delInput.placeholder = '비밀번호';
    var btns = document.createElement('div'); btns.className = '__fw-gbdel-btns';
    var cancel = document.createElement('button'); cancel.type = 'button'; cancel.textContent = '취소';
    delConfirm = document.createElement('button'); delConfirm.type = 'submit';
    delConfirm.className = '__fw-gbdel-confirm'; delConfirm.textContent = '삭제';
    btns.appendChild(cancel); btns.appendChild(delConfirm);
    form.appendChild(delInput); form.appendChild(btns);
    modal.appendChild(title); modal.appendChild(desc); modal.appendChild(form);
    delOverlay.appendChild(modal);
    document.body.appendChild(delOverlay);

    cancel.addEventListener('click', closeDeleteModal);
    delOverlay.addEventListener('click', function (e) { if (e.target === delOverlay) closeDeleteModal(); });
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submitDelete();
    });
  }

  function openDeleteModal(item) {
    ensureDeleteModal();
    delTarget = item;
    delInput.value = '';
    delConfirm.disabled = false; delConfirm.textContent = '삭제';
    delOverlay.classList.add('active');
    if (window.__fwScrollLock) window.__fwScrollLock.lock({ allowScrollWithin: delOverlay });
    else document.body.style.overflow = 'hidden';
    delInput.focus();
  }

  function closeDeleteModal() {
    if (!delOverlay) return;
    delOverlay.classList.remove('active');
    delTarget = null;
    if (window.__fwScrollLock) window.__fwScrollLock.unlock();
    else document.body.style.overflow = '';
  }

  function submitDelete() {
    if (!delTarget || delConfirm.disabled) return;
    var pw = delInput.value;
    if (!pw) { toast('비밀번호를 입력해주세요.'); return; }
    var item = delTarget;
    delConfirm.disabled = true; delConfirm.textContent = '삭제 중...';
    fetch(API_BASE + '/guestbook/' + encodeURIComponent(item.id) + '/delete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password: pw }),
    })
      .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, status: res.status, data: d }; }); })
      .then(function (r) {
        delConfirm.disabled = false; delConfirm.textContent = '삭제';
        if (r.ok && r.data.success) {
          closeDeleteModal();
          toast('방명록이 삭제되었습니다.');
          // 현재 페이지의 마지막 항목을 지웠으면 이전 페이지로 이동
          var list = findList();
          var remaining = list ? list.children.length - 1 : 0;
          if (remaining <= 0 && page > 1) page--;
          load();
        } else if (r.status === 429) { toast('요청 횟수를 초과했습니다. 잠시 후 다시 시도해주세요.'); }
        else { toast((r.data && r.data.error && r.data.error.message) || '비밀번호가 일치하지 않습니다.'); }
      })
      .catch(function () {
        delConfirm.disabled = false; delConfirm.textContent = '삭제';
        toast('네트워크 오류가 발생했습니다.');
      });
  }

  function onSubmit(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    var form = findForm(); if (!form) return;
    if (MODE === 'preview') { toast('미리보기입니다. 청첩장을 생성하면 실제로 저장됩니다.'); form.reset(); closeModal(); return; }
    if (MODE === 'disabled' || !API_BASE) { toast('청첩장을 먼저 생성해주세요.'); return; }
    var fd = new FormData(form);
    var name = (fd.get('name') || '').toString().trim();
    var message = (fd.get('message') || '').toString().trim();
    var password = (fd.get('password') || '').toString();
    if (!name || !message) { toast('이름과 메시지를 입력해주세요.'); return; }
    if (!password) { toast('비밀번호를 입력해주세요.'); return; }
    var btn = form.querySelector('button[type="submit"]') || form.querySelector('.rsvp-submit');
    var orig = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = '작성 중...'; }
    fetch(API_BASE + '/guestbook', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: name, message: message, password: password, isPrivate: false }),
    })
      .then(function (res) { return res.json().then(function (d) { return { ok: res.ok, status: res.status, data: d }; }); })
      .then(function (r) {
        if (btn) { btn.disabled = false; btn.textContent = orig; }
        if (r.ok && r.data.success) {
          toast('방명록이 등록되었습니다.');
          form.reset(); closeModal();
          page = 1; load();
        } else if (r.status === 429) { toast('제출 횟수를 초과했습니다.'); }
        else { toast((r.data && r.data.error && r.data.error.message) || '방명록 등록에 실패했습니다.'); }
      })
      .catch(function () {
        if (btn) { btn.disabled = false; btn.textContent = orig; }
        toast('네트워크 오류가 발생했습니다.');
      });
  }

  function init() {
    var form = findForm();
    if (form && !form.dataset.fwGbInit) {
      form.dataset.fwGbInit = '1';
      form.addEventListener('submit', onSubmit, true);
    }
    var openBtn = findOpen();
    if (openBtn && !openBtn.dataset.fwGbOpenInit) {
      openBtn.dataset.fwGbOpenInit = '1';
      openBtn.addEventListener('click', function (e) { e.preventDefault(); openModal(); });
    }
    var closeBtn = findClose();
    if (closeBtn && !closeBtn.dataset.fwGbCloseInit) {
      closeBtn.dataset.fwGbCloseInit = '1';
      closeBtn.addEventListener('click', closeModal);
    }
    var ov = findOverlay();
    if (ov && !ov.dataset.fwGbOverlayInit) {
      ov.dataset.fwGbOverlayInit = '1';
      ov.addEventListener('click', function (e) { if (e.target === ov) closeModal(); });
    }
    load();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
</script>
`;
//# sourceMappingURL=guestbook.js.map